import { useId, type CSSProperties, type ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';

type ShieldShapeOrientation = 'up' | 'down';
type ShieldShapeSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const ORIENTATION_ROTATION: Record<ShieldShapeOrientation, number> = {
  up: 0,
  down: 180,
};

const ICON_INSET: Record<ShieldShapeOrientation, CSSProperties> = {
  down: { top: 0, right: 0, bottom: '15%', left: 0 },
  up: { top: '15%', right: 0, bottom: 0, left: 0 },
};

const SIZE_PX: Record<ShieldShapeSize, number> = {
  xxs: 32,
  xs: 44,
  sm: 54,
  md: 64,
  lg: 80,
  xl: 96,
};

const BORDER_WIDTH: Record<ShieldShapeSize, number> = {
  xxs: 1,
  xs: 1,
  sm: 1,
  md: 2,
  lg: 2,
  xl: 2,
};

const SHAPE_PATH =
  'M8.71313 61.5947L53.0227 61.5947C54.3601 61.5947 55.609 60.9262 56.3508 59.8135L61.5422 52.0274C62.3644 50.794 62.4367 49.2067 61.7307 47.9033L37.4592 3.09473C36.7601 1.80418 35.4104 1.00013 33.9426 1.00001L29.4661 1.00001C27.9981 1.00001 26.6477 1.80396 25.9485 3.09473L1.48267 48.2627C0.860998 49.4104 0.838396 50.7896 1.42212 51.957L5.13501 59.3838C5.81257 60.7389 7.19806 61.5947 8.71313 61.5947Z';

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
  width: var(--shape-size);
  height: var(--shape-size);

  &:hover .shield-bottom-glow {
    opacity: 1;
  }
`;

const ShapeSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;

  .shield-bottom-glow {
    opacity: 0;
    transition: opacity 0.2s ease;
  }
`;

const IconWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  justify-content: center;
`;

type Props = {
  borderColor?: string;
  background?: string;
  icon?: ReactNode;
  orientation?: ShieldShapeOrientation;
  size?: ShieldShapeSize;
  className?: string;
  style?: CSSProperties;
};

export const ShieldShape = ({
  borderColor = 'var(--c-nd-150)',
  background = 'var(--c-nl-800)',
  icon,
  orientation = 'down',
  size = 'md',
  className,
  style,
}: Props) => {
  const gradientId = useId();
  const rotation = ORIENTATION_ROTATION[orientation];
  const sizePx = SIZE_PX[size];
  const isNotRotated = rotation === 0;

  return (
    <Root
      className={className}
      style={{ '--shape-size': `${sizePx}px`, ...style } as CSSProperties}
    >
      <ShapeSvg
        viewBox="0 0 64 63"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: rotation !== 0 ? `rotate(${rotation}deg)` : undefined,
          color: borderColor,
        }}
      >
        {!!isNotRotated && (
          <defs>
            <radialGradient
              id={gradientId}
              cx="0.5"
              cy="1"
              r="0.5"
              gradientUnits="objectBoundingBox"
            >
              <stop
                offset="0%"
                style={{ stopColor: 'currentColor', stopOpacity: 0.35 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: 'currentColor', stopOpacity: 0 }}
              />
            </radialGradient>
          </defs>
        )}
        <path
          d={SHAPE_PATH}
          fill={background}
          stroke={borderColor}
          strokeWidth={BORDER_WIDTH[size]}
          vectorEffect="non-scaling-stroke"
        />
        {!!isNotRotated && (
          <path
            d={SHAPE_PATH}
            fill={`url(#${gradientId})`}
            className="shield-bottom-glow"
          />
        )}
      </ShapeSvg>
      {!!icon && (
        <IconWrapper style={ICON_INSET[orientation]}>{icon}</IconWrapper>
      )}
    </Root>
  );
};
